import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit/dist/hooks";
import { AddCircleOutline, SearchOutlined } from "@mui/icons-material";
import { Box, Button, Grid, IconButton, InputAdornment, TextField } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { _sessionManager } from "api/ApiHelper";
import { AppContext } from "App";
import { DeleteIcon, SystemIcon } from "components/Icons/Icons";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { enqueueSnackbar } from "notistack";
import { v4 } from "uuid";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import AgentAddEditDialog from "./AgentAddEditDialog";

export default function AgentListWorkspace() {
  const { state } = useLocation();
  const { agency_id } = useParams();
  const { globalState } = useContext(AppContext);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const { component: confirmDialogComponent, actions: confirmDialog } = useDialog();
  const { addAttachments } = useChatAssistantAttachments();

  const getApi = useCallback(() => {
    return new ApiHelper({
      baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
      config: {},
      sessionManager: _sessionManager,
      _instance: null,
    });
  }, []);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const api = getApi();
      const filter = { agency_id, is_active: true };
      const agents = await api.callPost("agent/search", filter, globalState);
      setRows(agents);
    } catch (err) {
      console.error("Error loading agents", err);
    } finally {
      setLoading(false);
    }
  }, [agency_id, getApi, globalState]);

  const responseMessage = (err) => {
    const detail = err?.response?.data?.detail || err?.detail || "";
    const status = err?.response?.status;
    const messageFromServer = err?.message || "";

    if (detail.includes("Cannot insert duplicate key")) {
      return "Duplicate entry: This agent already exists.";
    }

    if (status === 500) {
      return "Server error. Please contact support.";
    }

    if (messageFromServer) {
      return messageFromServer;
    }

    return "Something went wrong. Please try again.";
  };

  const handleNotificationMessage = (message, variant) => {
    const _message = variant === "error" ? responseMessage(message) : message;
    enqueueSnackbar(_message, { preventDuplicate: true, variant });
  };

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const AddAgent = useCallback(
    async (agent) => {
      const memberId = agent.member_id ?? agent.Member_id;
      if (!memberId) {
        handleNotificationMessage("Select a member before adding an agent.", "error");
        return;
      }
      // Explicit Agent row fields only — do not spread Member display fields / Member.id
      const payload = {
        id: v4(),
        member_id: memberId,
        agency_id,
        is_active: true,
      };
      try {
        const api = getApi();
        await api.callPost("agent", payload, globalState);
        await loadAgents();
      } catch (err) {
        handleNotificationMessage(err, "error");
      }
    },
    [agency_id, getApi, globalState, loadAgents]
  );

  const DeleteAgent = useCallback(
    async (agent) => {
      const agentId = agent.id ?? agent.Id;
      const memberId = agent.member_id ?? agent.Member_id;
      const agencyId = agent.agency_id ?? agent.Agency_id ?? agency_id;
      if (!agentId || !memberId || !agencyId) {
        handleNotificationMessage(
          "Cannot delete agent: missing Agent id / member_id / agency_id. Refresh and try again.",
          "error"
        );
        return;
      }
      // Soft-delete must send Agent.id (not Member.id) + member_id + agency_id
      const payload = {
        id: agentId,
        member_id: memberId,
        agency_id: agencyId,
        is_active: false,
      };
      try {
        const api = getApi();
        await api.callPost("agent", payload, globalState);
        await loadAgents();
      } catch (err) {
        handleNotificationMessage(err, "error");
      }
    },
    [agency_id, getApi, globalState, loadAgents]
  );

  const handleDelete = useCallback(
    (agent) => {
      confirmDialog.confirm({
        title: "Delete this agent?",
        prompt: "Are you sure you want to delete this agent?",
        callback: () => DeleteAgent(agent),
      });
    },
    [confirmDialog, DeleteAgent]
  );

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(({ first_name, last_name, business_name, email }) =>
      [first_name, last_name, business_name, email].some((field) => (field ?? "").toLowerCase().includes(term))
    );
  }, [rows, searchTerm]);

  useEffect(() => {
    addAttachments([{ name: `Agents ${state?.name?.toLowerCase()}`, content: filteredRows }]);
  }, [filteredRows, state?.name]);

  const handleSave = useCallback(
    (agent) => {
      AddAgent(agent);
      setEditingAgent(null);
    },
    [AddAgent]
  );

  const columns = useMemo(
    () => [
      { field: "first_name", headerName: "First Name", flex: 1, minWidth: 150 },
      { field: "last_name", headerName: "Last Name", flex: 1, minWidth: 150 },
      { field: "business_name", headerName: "Business Name", flex: 1, minWidth: 150 },
      { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDelete(params.row)}
          />,
        ],
      },
    ],
    [handleDelete]
  );

  return (
    <>
      <WorkspaceHeader
        title={`Agents ${state?.name?.toLowerCase()}`}
        category={{
          title: "System",
          icon: <SystemIcon />,
        }}
        breadCrumbs={[{ title: "Agencies", path: "/system/agencies" }]}
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <SearchOutlined />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ height: 500, width: "100%" }}>
        <TilleyDataGrid
          rows={filteredRows}
          columns={columns}
          pageSizeOptions={[5, 10, 15, 20]}
          defaultPageSize={10}
          getRowId={(row) => row.id}
          isLoading={loading}
        />
      </Box>

      <WorkspaceActionBar
        content={
          <Box>
            <Button
              variant="contained"
              sx={{ width: "100%" }}
              startIcon={<AddCircleOutline />}
              onClick={() => setEditingAgent({})}
            >
              New Agent
            </Button>
          </Box>
        }
      />

      <AgentAddEditDialog
        open={Boolean(editingAgent)}
        addEditAgent={editingAgent}
        onChange={(agent) => {
          handleSave(agent);
        }}
        onClose={() => setEditingAgent(null)}
      />

      {confirmDialogComponent}
    </>
  );
}
